import { TEMPLATES } from '@/components/templates/registry';
import type { Fiche, TemplateId } from '@pharmafiche/schema';
import { useEffect, useRef } from 'react';

interface TemplatePickerProps {
  fiche: Fiche;
  currentId: TemplateId;
  onPick: (id: TemplateId) => void;
  onClose: () => void;
}

/**
 * Modal that surfaces the 6 templates as cards. Each card is a 200×280 px
 * miniature; clicking it switches the fiche's template_id via editorStore.
 *
 * For Phase 6 v1 the miniatures are stylised summaries (gradient swatch + name
 * + vibe). Phase 9 polish will swap them for live React-renders of the fiche
 * at miniature scale.
 */
export function TemplatePicker({ fiche, currentId, onPick, onClose }: TemplatePickerProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
      // biome-ignore lint/a11y/useSemanticElements: native <dialog> doesn't compose with our shell layout
      role="dialog"
      aria-modal="true"
      aria-label="Choisir un template"
      data-testid="template-picker"
    >
      <div
        ref={ref}
        className="bg-paper border-[2.5px] border-ink rounded-modal max-w-[1080px] w-full max-h-[90vh] overflow-y-auto shadow-ink-lg"
      >
        <header className="px-6 py-4 border-b border-ink/20 flex items-center justify-between">
          <h2 className="font-gloock text-[22px] text-ink">Choisis un template</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-ink hover:bg-ink/10 rounded-full w-8 h-8 grid place-items-center"
          >
            ✕
          </button>
        </header>

        <div className="p-4 md:p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {TEMPLATES.map((t) => {
            const active = t.id === currentId;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onPick(t.id)}
                className={[
                  'text-left rounded-card overflow-hidden border-[2.5px] border-ink bg-white transition-transform hover:translate-y-[-2px]',
                  active ? 'ring-4 ring-accent-blue/60' : '',
                ].join(' ')}
                data-testid={`template-${t.id}`}
              >
                <TemplateMiniature id={t.id} fiche={fiche} />
                <div className="p-3 border-t-2 border-ink">
                  <div className="font-gloock text-[16px] text-ink flex items-center gap-2">
                    {t.label}
                    {active ? (
                      <span className="font-hand text-[12px] bg-accent-blue text-white px-2 py-[1px] rounded-full">
                        actif
                      </span>
                    ) : null}
                  </div>
                  <div className="font-caveat text-[14px] text-ink-soft mt-1">{t.vibe}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface MiniatureProps {
  id: TemplateId;
  fiche: Fiche;
}

/**
 * Stylised preview of each template, not a live render. Big-enough hint for
 * the student to pick the right vibe at a glance.
 */
function TemplateMiniature({ id, fiche }: MiniatureProps) {
  const title = fiche.meta.title;

  switch (id) {
    case 'dessinee':
      return (
        <div
          className="h-[150px] relative overflow-hidden"
          style={{
            background:
              '#fdfaf1 linear-gradient(#e7dec8 1px, transparent 1px), linear-gradient(90deg, #e7dec8 1px, transparent 1px)',
            backgroundSize: '14px 14px',
          }}
        >
          <div
            className="absolute top-3 left-1/2 -translate-x-1/2 bg-accent-yellow border-[2px] border-ink rounded-[8px] px-3 py-[2px] font-gloock text-[12px]"
            style={{
              transform: 'translateX(-50%) rotate(-1.5deg)',
              boxShadow: '2px 2px 0 #1f2330',
            }}
          >
            {truncate(title, 22)}
          </div>
          <div className="absolute top-[44px] left-3 right-3 grid grid-cols-2 gap-2">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="bg-white border-[1.5px] border-ink rounded-[6px] p-2"
                style={{ boxShadow: '2px 2px 0 #1f2330' }}
              >
                <div className="h-1.5 bg-accent-blue rounded mb-1" />
                <div className="h-1 bg-ink/30 rounded mb-1" />
                <div className="h-1 bg-ink/30 rounded w-3/4" />
              </div>
            ))}
          </div>
        </div>
      );
    case 'clinique':
      return (
        <div className="h-[150px] bg-white p-3 relative">
          <div className="absolute top-0 left-0 right-0 h-[6px] bg-ink" />
          <div className="text-[8px] uppercase tracking-[0.2em] text-ink-soft mt-2">Protocole</div>
          <div className="font-fraunces font-bold text-[14px] text-ink leading-tight">
            {truncate(title, 30)}
          </div>
          <div className="mt-2 pl-2 border-l-[3px] border-accent-blue">
            <div className="text-[8px] uppercase tracking-[0.15em] text-accent-blue font-source">
              01 · Indications
            </div>
            <div className="h-[2px] bg-ink/15 my-1" />
            <div className="h-[2px] bg-ink/40 rounded w-2/3 mb-[2px]" />
            <div className="h-[2px] bg-ink/30 rounded w-3/4" />
          </div>
          <div className="mt-2 pl-2 border-l-[3px] border-accent-red">
            <div className="text-[8px] uppercase tracking-[0.15em] text-accent-red font-source">
              02 · Posologie
            </div>
          </div>
        </div>
      );
    case 'infographique':
      return (
        <div className="h-[150px] grid grid-cols-2">
          <div className="bg-[#eaf2fb] p-2 flex flex-col">
            <div className="text-[7px] uppercase tracking-[0.2em] text-accent-blue">#01</div>
            <div className="font-fraunces font-bold text-[10px] leading-tight text-accent-blue">
              {truncate(title, 18)}
            </div>
            <div
              className="font-fraunces font-bold leading-none text-accent-blue mt-2"
              style={{ fontSize: 28 }}
            >
              90
            </div>
            <div className="text-[7px] uppercase tracking-wider text-ink-soft">mg × 2/j</div>
          </div>
          <div className="bg-[#fff8d6] p-2 flex flex-col">
            <div className="text-[7px] uppercase tracking-[0.2em] text-ink-soft">Mnémo</div>
            <div
              className="font-outfit text-[10px] mt-1 bg-yellow-100 inline-block px-2 py-[2px]"
              style={{ transform: 'rotate(-2deg)' }}
            >
              AAP = plaquettes
            </div>
          </div>
        </div>
      );
    case 'comparative':
      return (
        <div className="h-[150px] bg-white p-2">
          <div className="font-dm text-[12px] mb-1">{truncate(title, 28)}</div>
          <div className="grid grid-cols-4 gap-[2px] text-[7px]">
            <div />
            <div className="border-t-2 border-accent-blue pt-[2px] font-plex font-bold text-accent-blue">
              Asp
            </div>
            <div className="border-t-2 border-accent-green pt-[2px] font-plex font-bold text-accent-green">
              Clopi
            </div>
            <div className="border-t-2 border-accent-purple pt-[2px] font-plex font-bold text-accent-purple">
              Ticag
            </div>
            <div className="font-plex uppercase text-ink-soft">Méca</div>
            <div className="border-b border-ink/15">COX-1</div>
            <div className="border-b border-ink/15">P2Y12</div>
            <div className="border-b border-ink/15">P2Y12</div>
            <div className="font-plex uppercase text-ink-soft mt-[2px]">Poso</div>
            <div className="border-b border-ink/15 font-mono">75 mg</div>
            <div className="border-b border-ink/15 font-mono">75 mg</div>
            <div className="border-b border-ink/15 font-mono">90 ×2</div>
          </div>
        </div>
      );
    case 'flowchart':
      return (
        <div className="h-[150px] p-2" style={{ background: '#f7f8fa' }}>
          <div
            className="text-[7px] uppercase tracking-[0.2em] text-ink-soft"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            Mécanisme
          </div>
          <div className="font-space font-bold text-[11px] text-ink">{truncate(title, 24)}</div>
          <div className="mt-2 flex items-center justify-around">
            <div className="w-6 h-6 rounded-full bg-accent-green border-[1.5px] border-ink" />
            <div className="text-[10px]">→</div>
            <div className="w-6 h-6 rounded bg-accent-blue/30 border-[1.5px] border-ink" />
            <div className="text-[10px]">→</div>
            <div className="w-6 h-6 rounded-full bg-accent-red border-[1.5px] border-ink" />
          </div>
          <div className="mt-2 grid grid-cols-3 gap-1 text-[7px]">
            {['Asp', 'Clopi', 'Ticag'].map((d, i) => (
              <div
                key={d}
                className="bg-white p-1 rounded"
                style={{ borderLeft: '2px solid #1f2330' }}
              >
                {d}
              </div>
            ))}
          </div>
        </div>
      );
    case 'minimaliste':
      return (
        <div className="h-[150px] bg-white p-3 font-garamond">
          <div className="text-center font-bold text-[12px]">{truncate(title, 26)}</div>
          <div className="border-t border-ink/40 mx-auto w-10 mt-1 mb-2" />
          <div className="columns-2 gap-3 text-[7px] leading-[1.45]">
            <div className="font-bold uppercase tracking-wider text-[7px] border-b border-ink/30">
              01. Intro
            </div>
            <ul className="pl-2 list-disc">
              <li>Inhibition COX-1</li>
              <li>Indication SCA</li>
              <li>EI hémorragie</li>
            </ul>
            <div className="font-bold uppercase tracking-wider text-[7px] border-b border-ink/30 mt-1">
              02. Poso
            </div>
            <ul className="pl-2 list-disc">
              <li>75 mg/j</li>
              <li>325 mg/j</li>
            </ul>
          </div>
        </div>
      );
    default:
      return <div className="h-[150px] bg-paper" />;
  }
}

function truncate(text: string, n: number): string {
  return text.length > n ? `${text.slice(0, n - 1)}…` : text;
}
